import { useCallback, useRef } from 'react';

/**
 * Returned when the builder throws — invalid input is never "unchanged".
 *
 * Written as an ESCAPE, never as a literal NUL byte. A raw NUL makes git treat the whole
 * file as BINARY, which silently drops it from diffs and from code review — this file
 * shipped that way once. The escape keeps what the sentinel is for: `JSON.stringify` can
 * never emit a bare NUL, so it cannot collide with a real payload.
 */
const INVALID = '\u0000invalid';

const signature = (build: () => unknown): string => {
	try {
		return JSON.stringify(build()) ?? '';
	} catch {
		// A builder that throws is reporting invalid input. Treat that as dirty so the
		// section still attempts its save and the real validation message reaches the user,
		// rather than being silently skipped as "nothing changed".
		return INVALID;
	}
};

/**
 * Is this editor's payload different from the one it loaded with?
 *
 * Compares the ACTUAL request body — the same `build()` a save would send — against the
 * body captured on first render. Comparing payloads rather than form fields means the
 * answer is exactly "would this write change anything", so a cosmetic difference that
 * normalises away (`"5.00"` → `500`) correctly counts as unchanged.
 *
 * **Why this exists.** The four association rule sections (dues, late fees, statement,
 * report basis) each expose a `save(id)` handle that the edit page calls for every mounted
 * section. Their skip conditions only covered the CREATE flow — `if (draft && !hasDraftValue)`,
 * `if (!rule && !touched)`, `if (!association && choice === 'none')`, and in one case nothing
 * at all — so in EDIT mode all four wrote unconditionally. Renaming an association fired
 * four extra writes, and for a user without accounting write permission two of them
 * answered 403, turning an untouched section into a failed save the user never asked for.
 *
 * Capture on first render is safe for these sections because each parent renders a spinner
 * until its data has loaded, so the state initialisers have already seen real values. An
 * editor that mounts before its data arrives must call `markSaved()` once seeded instead.
 */
export function usePristinePayload(build: () => unknown): { isDirty: boolean; markSaved: () => void } {
	const pristine = useRef<string | null>(null);
	const current = signature(build);
	if (pristine.current === null) pristine.current = current;

	/** Re-baseline after a successful write, so a second Save is not a second request. */
	const markSaved = useCallback(() => { pristine.current = signature(build); }, [build]);

	return { isDirty: current !== pristine.current, markSaved };
}
